// ============================================================================
// ZONE HOOKS
// ============================================================================
// React hooks for Zone Master API operations.

import { useState, useEffect, useCallback } from 'react';
import { zoneService } from '../../api/services/zoneService';
import type {
  Zone,
  ZoneListItem,
  CreateZoneRequest,
  UpdateZoneRequest,
  ZoneSearchRequest,
} from '../../api/types/zone.types';
import type { ID } from '../../api/types';

/** List hook — paginated, re-fetches whenever params change. */
export const useZones = (params?: ZoneSearchRequest) => {
  const [data, setData] = useState<ZoneListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    totalPages: 0,
    totalItems: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  // Serialize params so object-literal callers don't trigger infinite re-renders.
  const paramsKey = JSON.stringify(params);

  const fetchZones = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await zoneService.getAll(params);
      setData(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => {
    fetchZones();
  }, [fetchZones]);

  return { data, loading, error, pagination, refetch: fetchZones };
};

/** Single-zone hook by ID. */
export const useZone = (id: ID | undefined) => {
  const [data, setData] = useState<Zone | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchZone = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const response = await zoneService.getById(id);
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchZone();
  }, [fetchZone]);

  return { data, loading, error, refetch: fetchZone };
};

/** Create mutation. */
export const useCreateZone = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const createZone = async (
    data: CreateZoneRequest,
    options?: { onSuccess?: (zone: Zone) => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true);
    setError(null);
    try {
      const response = await zoneService.create(data);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const e = err as Error;
      setError(e);
      options?.onError?.(e);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return { createZone, loading, error };
};

/** Update mutation. */
export const useUpdateZone = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const updateZone = async (
    id: ID,
    data: UpdateZoneRequest,
    options?: { onSuccess?: (zone: Zone) => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true);
    setError(null);
    try {
      const response = await zoneService.update(id, data);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const e = err as Error;
      setError(e);
      options?.onError?.(e);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return { updateZone, loading, error };
};

/** Delete mutation (soft delete). */
export const useDeleteZone = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const deleteZone = async (
    id: ID,
    options?: { onSuccess?: () => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true);
    setError(null);
    try {
      await zoneService.delete(id);
      options?.onSuccess?.();
    } catch (err) {
      const e = err as Error;
      setError(e);
      options?.onError?.(e);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return { deleteZone, loading, error };
};
